<template>
  <div>
    <b-card no-body class="mb-1">
      <b-card-header header-tag="header" class="p-0 mt-1">
        <b-btn block href="#" v-b-toggle.cirt variant="info" class="text-left">{{ dealName }} - CIRT</b-btn>
      </b-card-header>
      <b-collapse id="cirt" visible>
        <b-card style="border: none">
          <b-row class="mb-3">
            <b-col cols="3">
              <settings
                :inputOwnedFaceAmt="inputOwnedFaceAmt"
                @ownedFaceAmtChange="inputOwnedFaceAmt = $event"
                :inputMargin="inputMargin"
                @marginChange="inputMargin = $event"
                :inputInterestRate="inputInterestRate"
                @interestRateChange="inputInterestRate = $event"/>
              <table class="table table-sm color">
                <tbody>
                  <tr>
                    <th class="text-left font-weight-normal w-50" style=""></th>
                    <td class="pl-0 pr-0">
                      <b-btn variant="primary" class="mb-1 w-100" v-on:click="runCalculation({isSingle: true})">Run Scenario</b-btn>
                      <b-btn v-if="isAdmin" variant="primary" class="mb-1 w-100" v-on:click="runCalculation({isSingle: true, includeHistorical: true})">Run Scenario (Blended) </b-btn>
                      <b-btn variant="primary" class="mb-0 w-100" v-on:click="runCalculation({isSingle: false})">Run Matrix</b-btn>
                    </td>
                  </tr>
                </tbody>
              </table>
              <b-row>
                <b-alert v-if="this.errorMessage" variant="danger" class="float-left" show>{{ this.errorMessage }}</b-alert>
              </b-row>
              <SingleScenarioResult
                caption="CIRT"
                type="R-O-I"
                :result="this.yield"
                :wal="wal"
                :principalWindow="lossWindow"
                :writedownPct="writedownPct"
                :firstLossPeriod="firstLossPeriod"/>
              <LineMetrics :lineMetrics="lineMetrics"/>
              <PolicyInformation
                :liabilityLimit="policyInformation.liabilityLimit"
                :liabilityLimitPct="policyInformation.liabilityLimitPct"
                :retentionLimit="policyInformation.retentionLimit"
                :retentionLimitPct="policyInformation.retentionLimitPct"
                :initialCollateral="policyInformation.initialCollateral"
                :initialCollateralPct="policyInformation.initialCollateralPct"
                :initialPremium="policyInformation.initialPremium"
                :initialPremiumPct="policyInformation.initialPremiumPct"
                :ownership="policyInformation.ownership"
                :ownershipPct="policyInformation.ownershipPct"/>
            </b-col>
            <b-col cols="9">
              <b-tabs>
                <b-tab @click="clearErrorMessage()" title="ROI" active>
                  <matrix :matrixName="'ROI'" :matrixUnits="'%'" :cpr="matrixCpr" :cdr="matrixCdr"
                          :matrix="yieldMatrix" @matrixCellClicked="onMatrixCellClicked" :vectors="vectorNames"/>
                </b-tab>
                <b-tab @click="clearErrorMessage()" title="Rate On Line">
                  <matrix :matrixName="'Rate On Line'" :matrixUnits="'%'" :cpr="matrixCpr" :cdr="matrixCdr"
                          :matrix="rateOnLineMatrix" @matrixCellClicked="onMatrixCellClicked" :vectors="vectorNames"/>
                </b-tab>
                <b-tab @click="clearErrorMessage()" title="Loss On Line">
                  <matrix :matrixName="'Loss On Line'" :matrixUnits="'%'" :cpr="matrixCpr" :cdr="matrixCdr"
                          :matrix="lossOnLineMatrix" @matrixCellClicked="onMatrixCellClicked" :vectors="vectorNames"/>
                </b-tab>
                <b-tab @click="clearErrorMessage()" title="Margin On Line">
                  <matrix :matrixName="'Margin On Line'" :matrixUnits="'%'" :cpr="matrixCpr" :cdr="matrixCdr"
                          :matrix="returnOnLineMatrix" @matrixCellClicked="onMatrixCellClicked" :vectors="vectorNames"/>
                </b-tab>
              </b-tabs>
            </b-col>
          </b-row>
          <b-row class="mb-2 mt-2 ml-0">
            <b-btn v-if="cirtCashflow && cirtCashflow.length > 0" variant="info" class="float-left" @click="exportCashflowToExcel()">Export Cashflow to Excel</b-btn>
          </b-row>
          <cashflow ref="cashflow"
                    :cashflow="cirtCashflow"
                    :miscVariables="miscVariables"
                    :totals="totals"
                    :dealName="dealName"
                    :isMatrixClicked="matrixCellClicked"
                    :cpr="cprToPass"
                    :cdr="cdrToPass"/>
        </b-card>
      </b-collapse>
    </b-card>
  </div>
</template>

<script>
import service              from '../../services/HttpRequestService';
import formatter            from '../../utilities/Formatter';
import {createEmpty2dArray} from '@/utilities/ArrayUtils';
import {isNotEmpty2dArray}  from '@/utilities/ArrayUtils';
import moment               from 'moment';
import SingleScenarioResult from '../SingleScenarioResult';
import LineMetrics          from '../LineMetrics';
import Matrix               from '../Matrix';
import Settings             from './Settings';
import Cashflow             from './Cashflow';
import PolicyInformation    from './PolicyInformation';
import {
  isCirtAnalysisBodyValid,
  createCirtValidationErrorMessageFromBody
}                           from '@/validators/inputValidator';
import RequestBodyFactory   from '@/services/RequestBodyFactory';
import excelSupport         from '@/utilities/ExcelSupport';
import {TRACE_ERROR}        from '@/constants';
import {mapGetters}         from 'vuex'

export default {
  props: [
    'dealName',
    'activityMonth',
    'cpr',
    'cdr',
    'lossSeverity',
    'delinquency',
    'libor',
    'cancellation',
    'cancellationDate',
    'hpi',
    'NationalUnemploymentForecast',
    'hpa',
    'twoYrTsy',
    'tenYrTsy',
    'tuneCpr',
    'tuneCdr',
    'tuneSeverity',
    'mtg15',
    'mtg30',
    'selectedScenario'
  ],
  data() {
    return {
      inputOwnedFaceAmt:  '',
      inputMargin:        '',
      inputInterestRate:  0.0,
      cirtCashflow:       [],
      miscVariables:      [],
      policyInformation:  {},
      errorMessage:       '',
      yield:              '',
      wal:                '',
      lossWindow:         '',
      firstLossPeriod:    '',
      writedownPct:       '',
      matrixCpr:          [1, 2, 3],
      matrixCdr:          [1, 2, 3],
      yieldMatrix:        [[]],
      rateOnLineMatrix:   [[]],
      lossOnLineMatrix:   [[]],
      returnOnLineMatrix: [[]],
      totals:             {},
      lineMetrics:        {},
      forceUpdate:        false,
      matrixCellClicked:  false,
      cprToPass:          0,
      cdrToPass:          0,
      runDate:            null,
      vectors:            new Map(),
      vectorNames:        []
    };
  },
  async created() {
    readCookies(this);
    setDataFromAppData(this);
    let res = await service.getUserVectors();
    if (res.status == 200) {
      let vectors = res.data;
      for (let vector of vectors) {
        this.vectors.set(vector.Name, vector.Value.join(','));
        this.vectorNames.push(vector.Name);
      }
    }
  },
  computed:   {
    ...mapGetters([
      'useLoanLevel'
    ]),
    isAdmin() {
      return this.$store.getters.isAdmin;
    }
  },
  methods: {
    async runCalculation({
      isSingle = true,
      cpr = null,
      cdr = null,
      includeHistorical = false } = {}) {
      if (cpr == null && cdr == null) {
        this.matrixCellClicked = false;
      }

      this.clearErrorMessage();
      this.$emit('inputError', '');
      this.clearResults();

      let cprToPass = [];
      let cdrToPass = [];

      if (isSingle && cpr != null) {
        const regex = RegExp('.*[a-zA-Z]+.*');
        if (typeof cpr === 'string' && regex.test(cpr)) {
          if (this.vectors.has(cpr)) {
            cpr = this.vectors.get(cpr);
          } else {
            this.errorMessage = cpr + ' vector was not found.';
            return;
          }
        }
        if (typeof cdr === 'string' && regex.test(cdr)) {
          if (this.vectors.has(cdr)) {
            cdr = this.vectors.get(cdr);
          } else {
            this.errorMessage = cdr + ' vector was not found.';
            return;
          }
        }
      } else {
        const regex = RegExp('.*[a-zA-Z]+.*');
        for (let i = 0; i < this.matrixCpr.length; i++) {
          if (typeof this.matrixCpr[i] === 'string' && regex.test(this.matrixCpr[i])) {
            if (this.vectors.has(this.matrixCpr[i])) {
              cprToPass.push(this.vectors.get(this.matrixCpr[i]));
            } else {
              this.errorMessage = this.matrixCpr[i] + ' vector was not found.';
              return;
            }
          } else {
            cprToPass.push(this.matrixCpr[i]);
          }
        }

        for (let i = 0; i < this.matrixCdr.length; i++) {
          if (typeof this.matrixCdr[i] === 'string' && regex.test(this.matrixCdr[i])) {
            if (this.vectors.has(this.matrixCdr[i])) {
              cdrToPass.push(this.vectors.get(this.matrixCdr[i]));
            } else {
              this.errorMessage = this.matrixCdr[i] + ' vector was not found.';
              return;
            }
          } else {
            cdrToPass.push(this.matrixCdr[i]);
          }
        }
      }

      try {
        this.$emit('show-spinner');
        let ownedFaceAmt  = Number(this.inputOwnedFaceAmt.toString().replace(/,/g, ''));
        let activityMonth = moment(this.activityMonth, 'MM/YYYY');
        let updDate       = moment()
          .year(activityMonth.year())
          .month(activityMonth.month())
          .date(activityMonth.daysInMonth());

        let reqBody = new RequestBodyFactory(
          'CIRT',
          {
            dealName:                     this.dealName,
            settlementDate:               updDate.format('YYYYMMDD'),
            cpr:                          isSingle ? cpr || this.cpr : cprToPass,
            cdr:                          isSingle ? cdr || this.cdr : cdrToPass,
            lossSeverity:                 this.lossSeverity,
            delinquency:                  this.delinquency,
            ownedFaceAmt:                 ownedFaceAmt,
            margin:                       this.inputMargin / 100,
            interestRate:                 this.inputInterestRate / 100,
            cancellation:                 this.cancellation,
            cancellationDate:             this.cancellationDate,
            runLoanLevel:                 this.useLoanLevel,
            prepaymentModel:              this.$store.getters.prepaymentModel,
            hpi:                          this.hpi,
            NationalUnemploymentForecast: this.NationalUnemploymentForecast,
            hpa:                          this.hpa,
            twoYrTsy:                     this.twoYrTsy,
            tenYrTsy:                     this.tenYrTsy,
            tuneCpr:                      this.tuneCpr,
            tuneCdr:                      this.tuneCdr,
            tuneSeverity:                 this.tuneSeverity,
            mtg15:                        this.mtg15,
            mtg30:                        this.mtg30,
            selectedScenario:             this.selectedScenario
          },
          isSingle
        );

        isSingle
          ? await this.runSingleCalculation(reqBody, { includeHistorical })
          : await this.runMatrixCalculation(reqBody);
        this.runDate = new Date();
      } catch (err) {
        this.errorMessage = err.response.data.Message;
        await service.log(TRACE_ERROR, this.errorMessage, err.stack);
      } finally {
        this.$emit('hide-spinner');
      }
    },

    async runSingleCalculation(body, config) {
      try {
        if (body.PrepaymentModel == 'UserDefined' && !isCirtAnalysisBodyValid(body)) {
          this.errorMessage = createCirtValidationErrorMessageFromBody(body);
          return;
        }
        setCookies(this);

        await this.makeRequest(body, config);

        this.$store.commit('saveCirtSingleResult', {
          Yield:                this.yield,
          Wal:                  this.wal,
          LossWindow:           this.lossWindow,
          WritedownPct:         this.writedownPct,
          FirstLossPeriod:      this.firstLossPeriod,
          cirtCashflow:         this.cirtCashflow,
          insurerOwnedCashflow: this.insurerOwnedCashflow,
          LineMetrics:          this.lineMetrics,
          PolicyInformation:    this.policyInformation
        });

      } catch (err) {
        const genericErrorMessage = `Failed to generate CIRT cashflow. ${err?.message}`;
        const { data: { Message : errorMessage = genericErrorMessage } = {} } = err?.response ?? {};
        this.errorMessage = errorMessage;
        await service.log(TRACE_ERROR, this.errorMessage, err.stack);
      }
    },

    async runMatrixCalculation(body) {
      let yieldMatrix        = createEmpty2dArray(10);
      let rateOnLineMatrix   = createEmpty2dArray(10);
      let lossOnLineMatrix   = createEmpty2dArray(10);
      let returnOnLineMatrix = createEmpty2dArray(10);

      try {
        if (body.PrepaymentModel == 'UserDefined' && !isCirtAnalysisBodyValid(body)) {
          this.errorMessage = createCirtValidationErrorMessageFromBody(body);
          return;
        }

        let res    = await service.post('simulate/cirt/matrix', body);
        let yields = res.data.Yield;

        let cprIndex = 0;
        let cdrIndex = 0;
        for (let matrixCdrIndex = 0; matrixCdrIndex < this.matrixCdr.length; matrixCdrIndex++) {
          if (
            this.matrixCdr[matrixCdrIndex] === '' ||
            this.matrixCdr[matrixCdrIndex] == undefined
          ) {
            continue;
          }

          let row            = yields[cdrIndex];
          let lineMetricsRow = res.data.LineMetrics[cdrIndex];

          for (let matrixCprIndex = 0; matrixCprIndex < this.matrixCpr.length; matrixCprIndex++) {
            if (
              this.matrixCpr[matrixCprIndex] === '' ||
              this.matrixCpr[matrixCprIndex] == undefined
            ) {
              continue;
            }

            let yieldResult       = row[cprIndex];
            let lineMetricsResult = lineMetricsRow[cprIndex];

            let value = '';
            if (yieldResult.IsSuccessful) {
              value = formatter.formatPctValue(yieldResult.Value, 2);
            } else {
              value = '-----';
              // TODO sk: remove after redesigning c++ message system
              if (
                yieldResult.ErrorMessage !== "Can't compute Yield." &&
                yieldResult.ErrorMessage !== 'Too many tries in Yield calculation.' &&
                yieldResult.ErrorMessage !== 'Guess is too negative in Yield calculation.' &&
                yieldResult.ErrorMessage !== "Can't compute Spread." &&
                yieldResult.ErrorMessage !== 'Too many tries in Spread calculation.'
              ) {
                this.errorMessage = yieldResult.ErrorMessage;
              }
            }
            yieldMatrix[matrixCprIndex][matrixCdrIndex]        = value;
            rateOnLineMatrix[matrixCprIndex][matrixCdrIndex]   = formatter.formatPctValue(
              lineMetricsResult.RateOnLine,
              2
            );
            lossOnLineMatrix[matrixCprIndex][matrixCdrIndex]   = formatter.formatPctValue(
              lineMetricsResult.LossOnLine,
              2
            );
            returnOnLineMatrix[matrixCprIndex][matrixCdrIndex] = formatter.formatPctValue(
              lineMetricsResult.ReturnOnLine,
              2
            );
            cprIndex++;
          }
          cprIndex = 0;
          cdrIndex++;
        }
      } catch (err) {
        console.log(err);

        this.errorMessage = err.response.data;
        await service.log(TRACE_ERROR, this.errorMessage, err.stack);
        yieldMatrix        = createEmpty2dArray(10);
        rateOnLineMatrix   = createEmpty2dArray(10);
        lossOnLineMatrix   = createEmpty2dArray(10);
        returnOnLineMatrix = createEmpty2dArray(10);
      }
      this.yieldMatrix        = yieldMatrix;
      this.rateOnLineMatrix   = rateOnLineMatrix;
      this.lossOnLineMatrix   = lossOnLineMatrix;
      this.returnOnLineMatrix = returnOnLineMatrix;
      this.$store.commit('saveCirtMatrixResult', {
        YieldMatrix:        yieldMatrix,
        RateOnLineMatrix:   rateOnLineMatrix,
        LossOnLineMatrix:   lossOnLineMatrix,
        ReturnOnLineMatrix: returnOnLineMatrix
      });
    },

    onMatrixCellClicked(payload) {
      const {cpr, cdr}       = payload;
      this.matrixCellClicked = true;
      this.cprToPass         = cpr;
      this.cdrToPass         = cdr;
      this.runCalculation(true, cpr, cdr);
    },

    async makeRequest(reqBody, { includeHistorical = false } = {}) {
      const res = await service.post(`simulate/cirt?includeHistorical=${includeHistorical}`, reqBody);

      const { data, data : { CirtCashflow: cirtCashflow = [] } = {}} = res ?? {};
      if (!data) {
        return;
      }

      this.cirtCashflow = formatter.formatDataInArray(
        cirtCashflow,
        [
          'CollateralBalance',
          'CollateralLosses',
          'RemainingLiability',
          'ReductionInLiability',
          'InsurerPaidLosses',
          'InsurancePremium',
          'CollateralTrustAccount',
          'CollateralAccountChange',
          'CollateralAccountInterest',
          'NetInsuranceCashflow'
        ],
        [],
        ['CprRate', 'CdrRate', 'LossSeverityRate', 'DelinquencyRate']
      );

      for (var row of this.cirtCashflow) {
        row['PaymentDate'] = formatter.formatDateValue(row['PaymentDate'], 'MM/YYYY');
      }

      this.miscVariables = res.data.MiscVariables || [];
      this.policyInformation = res.data.PolicyInformation || {};

      this.wal = res.data.Wal != null
        ? formatter.round(res.data.Wal)
        : formatter.formatDashesIfNeeded(res.data.Wal);
      this.lossWindow = res.data.LossWindow != null
        ? formatter.formatDashesIfNeeded(res.data.LossWindow)
        : ''
      this.writedownPct = res.data.WritedownPct != null
        ? formatter.formatPctValue(res.data.WritedownPct)
        : formatter.formatDashesIfNeeded(res.data.WritedownPct)
      this.firstLossPeriod = res.data.FirstLossPeriod != null
        ? res.data.FirstLossPeriod
        : ''
      this.totals = {
        CollateralLosses:     formatter.formatDollarValue(
          res.data.CirtCashflowTotals.CollateralLosses
        ),
        ReductionInLiability: formatter.formatDollarValue(
          res.data.CirtCashflowTotals.ReductionInLiability
        ),
        InsurerPaidLosses:    formatter.formatDollarValue(
          res.data.CirtCashflowTotals.InsurerPaidLosses
        ),
        InsurancePremium:     formatter.formatDollarValue(
          res.data.CirtCashflowTotals.InsurancePremium
        ),
        NetInsuranceCashflow: formatter.formatDollarValue(
          res.data.CirtCashflowTotals.NetInsuranceCashflow
        )
      };
      if (!res.data.LineMetrics) {
        res.data.LineMetrics = {}
      }
      this.lineMetrics       = {
        rateOnLine:   formatter.formatPctValue(res.data.LineMetrics.RateOnLine),
        lossOnLine:   formatter.formatPctValue(res.data.LineMetrics.LossOnLine),
        returnOnLine: formatter.formatPctValue(res.data.LineMetrics.ReturnOnLine)
      };

      if (!res.data.PolicyInformation) {
        res.data.PolicyInformation = {}
      }
      this.policyInformation = {
        liabilityLimit:       formatter.formatDollarValue(res.data.PolicyInformation.LiabilityLimit),
        liabilityLimitPct:    formatter.formatPctValue(res.data.PolicyInformation.LiabilityLimitPct),
        retentionLimit:       formatter.formatDollarValue(res.data.PolicyInformation.RetentionLimit),
        retentionLimitPct:    formatter.formatPctValue(res.data.PolicyInformation.RetentionLimitPct),
        initialCollateral:    formatter.formatDollarValue(
          res.data.PolicyInformation.InitialCollateral
        ),
        initialCollateralPct: formatter.formatPctValue(
          res.data.PolicyInformation.InitialCollateralPct
        ),
        initialPremium:       formatter.formatDollarValue(res.data.PolicyInformation.InitialPremium),
        initialPremiumPct:    formatter.formatPctValue(res.data.PolicyInformation.InitialPremiumPct),
        ownership:            formatter.formatDollarValue(res.data.PolicyInformation.Ownership),
        ownershipPct:         formatter.formatPctValue(res.data.PolicyInformation.OwnershipPct)
      };

      this.yield = formatter.formatDashesIfNeeded();
      if (res.data.Yield) {
        this.yield = res.data.Yield.IsSuccessful ?
          formatter.formatPctValue(res.data.Yield.Value) :
          formatter.formatDashesIfNeeded();
        if (!res.data.Yield.IsSuccessful) {
          this.errorMessage = res.data.Yield.ErrorMessage;
        }
      }
    },
    clearErrorMessage() {
      this.errorMessage = '';
    },
    clearResults() {
      this.cirtCashflow = [];
      this.yield        = '';
      this.wal          = '';
      this.lossWindow   = '';
      this.writedownPct = '';
    },
    exportCashflowToExcel() {
      return excelSupport.exportCashflowToExcel(
        this.$store.getters.username,
        this.dealName,
        null,
        this.activityMonth,
        this.runDate,
        this.selectedScenario ? this.selectedScenario.data : null,
        this.$refs.cashflow.toMatrix()
      );
    }
  },
  components: {
    SingleScenarioResult,
    LineMetrics,
    Settings,
    Cashflow,
    Matrix,
    PolicyInformation
  }
};

function readCookies(root) {
  root.inputOwnedFaceAmt = root.$cookies.isKey('cirtOwnedFaceAmt')
    ? root.$cookies.get('cirtOwnedFaceAmt')
    : '1,000,000';
  root.inputMargin       = root.$cookies.isKey('cirtMargin') ? root.$cookies.get('cirtMargin') : 20;
  root.inputInterestRate = root.$cookies.isKey('cirtInterestRate')
    ? root.$cookies.get('cirtInterestRate')
    : 0.0;
  root.matrixCpr         = root.$cookies.isKey('cirtMatrixCpr')
    ? JSON.parse(root.$cookies.get('cirtMatrixCpr'))
    : [0, 5, 10, 15, 20, 25, 30, 35];
  root.matrixCdr         = root.$cookies.isKey('cirtMatrixCdr')
    ? JSON.parse(root.$cookies.get('cirtMatrixCdr'))
    : [0, 0.25, 0.5, 1, 1.5, 2, 2.5, 3, 5];
}

function setDataFromAppData(root) {
  root.yield              = root.$store.getters.cirtSingleResult.yield;
  root.cirtCashflow       = root.$store.getters.cirtSingleResult.cirtCashflow;
  root.wal                = root.$store.getters.cirtSingleResult.wal;
  root.lossWindow         = root.$store.getters.cirtSingleResult.lossWindow;
  root.writedownPct       = root.$store.getters.cirtSingleResult.writedownPct;
  root.firstLossPeriod    = root.$store.getters.cirtSingleResult.firstLossPeriod;
  root.lineMetrics        = {
    rateOnLine:   root.$store.getters.cirtSingleResult.lineMetrics.rateOnLine,
    lossOnLine:   root.$store.getters.cirtSingleResult.lineMetrics.lossOnLine,
    returnOnLine: root.$store.getters.cirtSingleResult.lineMetrics.returnOnLine
  };
  root.policyInformation  = root.$store.getters.cirtSingleResult.policyInformation;
  root.yieldMatrix        = isNotEmpty2dArray(root.$store.getters.cirtMatrixResult.yieldMatrix)
    ? root.$store.getters.cirtMatrixResult.yieldMatrix
    : createEmpty2dArray(10);
  root.rateOnLineMatrix   = isNotEmpty2dArray(root.$store.getters.cirtMatrixResult.rateOnLineMatrix)
    ? root.$store.getters.cirtMatrixResult.rateOnLineMatrix
    : createEmpty2dArray(10);
  root.lossOnLineMatrix   = isNotEmpty2dArray(root.$store.getters.cirtMatrixResult.lossOnLineMatrix)
    ? root.$store.getters.cirtMatrixResult.lossOnLineMatrix
    : createEmpty2dArray(10);
  root.returnOnLineMatrix = isNotEmpty2dArray(
    root.$store.getters.cirtMatrixResult.returnOnLineMatrix
  )
    ? root.$store.getters.cirtMatrixResult.returnOnLineMatrix
    : createEmpty2dArray(10);
}

function setCookies(root) {
  root.$cookies.set('cirtOwnedFaceAmt', root.inputOwnedFaceAmt, '10d');
  if (root.inputMargin) {
    root.$cookies.set('cirtMargin', root.inputMargin, '10d');
  }
  if (root.inputInterestRate) {
    root.$cookies.set('cirtInterestRate', root.inputInterestRate, '10d');
  }
  if (root.matrixCpr.length) {
    root.$cookies.set('cirtMatrixCpr', JSON.stringify(root.matrixCpr), '10d');
  }
  if (root.matrixCdr.length) {
    root.$cookies.set('cirtMatrixCdr', JSON.stringify(root.matrixCdr), '10d');
  }
  root.$emit('setCookies');
}
</script>

<style lang="scss" scoped>
table {
  &.color {
    margin-bottom: 0;

    th {
      vertical-align: middle;
    }
  }

  &.no-border {
    th,
    td {
      border: none;
    }
  }
}

.form-control {
  font-size: 0.9rem;
  text-align: right;
}

.custom-select {
  border-radius: 0;
}

.no-focus {
  &:focus {
    outline: none;
  }
}
</style>
